import { api } from './api'

function sameCompany(a, b) {
  if (!a || !b) return false
  const aId = a.id != null ? String(a.id) : ''
  const bId = b.id != null ? String(b.id) : ''
  const aTenant = a.tenant_id != null ? String(a.tenant_id) : ''
  const bTenant = b.tenant_id != null ? String(b.tenant_id) : ''
  return (!!aId && aId === bId) || (!!aTenant && aTenant === bTenant)
}

/*Resuelve el rubro de una empresa probando los campos que el backend puede usar según su origen (alta manual, import, etc).*/
export function resolveCompanyType(company) {
  return (
    company?.business_type_id ??
    company?.businessTypeId ??
    company?.business_type?.id ??
    company?.business_type ??
    company?.businessType ??
    company?.typebusiness ??
    company?.type ??
    company?.description ??
    ''
  )
}

export const companiesService = {
  isSameCompany(companyA, companyB) {
    return sameCompany(companyA, companyB)
  },

  /*Obtiene todas las empresas del usuario*/
  async getAll() {
    try {
      const response = await api.get('/companies', true)
      return response
    } catch (error) {
      console.error('Error obteniendo empresas:', error)
      throw error
    }
  },

  /*Obtiene una empresa por su ID numérico. Si la API devuelve 404, se lanza sin log para no llenar consola.*/
  async getById(id) {
    try {
      const response = await api.get(`/companies/${id}`, true)
      return response
    } catch (error) {
      if (error?.status !== 404) console.error('Error obteniendo empresa:', error)
      throw error
    }
  },

  /*Obtiene una empresa por tenant_id (UUID). Útil si el backend solo expone GET por UUID y no por id numérico.*/
  async getByTenantId(tenantId) {
    if (!tenantId) throw new Error('tenant_id requerido')
    try {
      const response = await api.get(`/companies/${tenantId}`, true)
      return response
    } catch (error) {
      if (error?.status !== 404) console.error('Error obteniendo empresa por tenant:', error)
      throw error
    }
  },

  /*Crea una nueva empresa*/
  async create(companyData) {
    try {
      const payload = {
        name: companyData.name,
        description: companyData.description || '',
        opening_time: companyData.opening_time || '09:00:00',
        closing_time: companyData.closing_time || '18:00:00',
        country: companyData.country || 'MX',
        business_type_id: companyData.business_type_id || null,
        business_type_category: companyData.business_type_category || null,
        uses_appointments: companyData.uses_appointments !== undefined ? companyData.uses_appointments : true,
        typebusiness: companyData.typebusiness || null
      }
      // Opcionales: se omiten si vienen vacíos en vez de mandar '' (un email '' rebota isEmail en el backend).
      if (companyData.address) payload.address = companyData.address
      if (companyData.phone) payload.phone = companyData.phone
      if (companyData.email) payload.email = companyData.email

      const response = await api.post('/companies', payload, true)
      return response
    } catch (error) {
      console.error('Error creando empresa:', error)
      throw error
    }
  },

  /*Actualiza una empresa. El backend la identifica por el header x-company-tenant-id (empresa activa
    por default). Pasar options.tenantId para apuntar a OTRA empresa del usuario sin cambiar la activa
    (ver api.js put()) — requiere que el usuario tenga rol admin/owner ahí, si no el backend responde 403.*/
  async update(id, companyData, options = {}) {
    try {
      const response = await api.put('/companies', companyData, true, options)
      return response
    } catch (error) {
      console.error('Error actualizando empresa:', error)
      throw error
    }
  },

  async updateBranding(companyData) {
    try {
      const response = await api.put('/companies', companyData, true)
      return response
    } catch (error) {
      console.error('Error actualizando branding de empresa:', error)
      throw error
    }
  },

  async getMessageTemplates() {
    try {
      const response = await api.get('/companies/message-templates', true)
      return response?.data ?? response
    } catch (error) {
      console.error('Error obteniendo plantillas de mensajes:', error)
      throw error
    }
  },

  async updateMessageTemplates(templates) {
    try {
      const response = await api.put('/companies/message-templates', { templates }, true)
      return response?.data ?? response
    } catch (error) {
      console.error('Error actualizando plantillas de mensajes:', error)
      throw error
    }
  },

  async getPlatformFeatures() {
    try {
      const response = await api.get('/companies/features', true, { silentUpgrade: true })
      return response?.data ?? response
    } catch (error) {
      console.error('Error obteniendo features de plataforma:', error)
      throw error
    }
  },

  /*Activa una empresa (PATCH /companies/:id/activate)*/
  async activate(id) {
    try {
      const response = await api.patch(`/companies/${id}/activate`, {}, true)
      return response
    } catch (error) {
      console.error('Error activando empresa:', error)
      throw error
    }
  },

  /*Desactiva una empresa (PATCH /companies/:id/deactivate)*/
  async deactivate(id) {
    try {
      const response = await api.patch(`/companies/${id}/deactivate`, {}, true)
      return response
    } catch (error) {
      console.error('Error desactivando empresa:', error)
      throw error
    }
  },

  /*Elimina una empresa (DELETE /companies/:id)*/
  async delete(id) {
    try {
      const response = await api.delete(`/companies/${id}`, true)
      return response
    } catch (error) {
      console.error('Error eliminando empresa:', error)
      throw error
    }
  },

  /*Establece la empresa activa en localStorage*/
  setActiveCompany(company) {
    localStorage.setItem('active_company', JSON.stringify(company))
  },

  /*Obtiene la empresa activa de localStorage*/
  getActiveCompany() {
    const stored = localStorage.getItem('active_company')
    return stored ? JSON.parse(stored) : null
  },

  /*Resuelve el tenant_id de la empresa activa con la misma lógica que api.js getHeaders().
    Devuelve null cuando no hay empresa activa o no puede resolverse el tenant.*/
  getActiveTenantId() {
    const active = this.getActiveCompany()
    if (!active) return null
    if (active.tenant_id) return active.tenant_id
    if (active.id) {
      try {
        const list = localStorage.getItem('user_companies')
        if (list) {
          const companies = JSON.parse(list)
          const found = Array.isArray(companies) ? companies.find((c) => c.id === active.id) : null
          if (found?.tenant_id) return found.tenant_id
        }
      } catch (_) {}
    }
    return null
  },

  /*True cuando hay empresa activa con tenant resoluble (api.js enviará x-company-tenant-id).
    Usar como guard antes de disparar peticiones tenantizadas para no producir 400.*/
  hasActiveCompany() {
    return !!this.getActiveTenantId()
  },

  /*Actualiza parcialmente la empresa activa cacheada en localStorage*/
  patchActiveCompany(patch) {
    const active = this.getActiveCompany()
    if (!active) return null
    const merged = { ...active, ...patch }
    localStorage.setItem('active_company', JSON.stringify(merged))
    return merged
  },

  /*Guarda la lista de empresas en localStorage*/
  saveCompaniesToLocal(companies) {
    localStorage.setItem('user_companies', JSON.stringify(companies))
  },

  /*Obtiene las empresas de localStorage*/
  getCompaniesFromLocal() {
    const stored = localStorage.getItem('user_companies')
    return stored ? JSON.parse(stored) : []
  },

  /**
   * Carga empresas desde la API, guarda en localStorage, establece empresa activa
   * si no hay una, y retorna { companies, active }.
   * Se usa al iniciar sesión y al restaurar auth desde localStorage.
   */
  async loadAndSetInitialCompany() {
    try {
      const response = await api.get('/companies', true)
      const data = response.data || response
      const companiesData = Array.isArray(data) ? data : (data.companies || data.items || [])

      if (companiesData.length === 0) return { companies: [], active: null }

      const companies = companiesData.map(c => ({
        id: c.id,
        tenant_id: c.tenant_id || null,
        name: c.name,
        email: c.email || '',
        phone: c.phone || '',
        type: resolveCompanyType(c),
        logo: c.logo_url || c.logo || null,
        role: c.role || null,
        role_id: c.role_id ?? null,
        hierarchy_level: c.hierarchy_level ?? null,
        isActive: false
      }))

      let active = this.getActiveCompany()
      const found = active ? companies.find(c => sameCompany(c, active)) : null
      if (!found) {
        active = companies[0]
      } else {
        active = { ...found, ...active, name: found.name, tenant_id: found.tenant_id || active.tenant_id || null }
      }

      companies.forEach(c => { c.isActive = sameCompany(c, active) })
      this.setActiveCompany(active)
      this.saveCompaniesToLocal(companies)

      return { companies, active }
    } catch (error) {
      console.error('[CompaniesService] loadAndSetInitialCompany:', error)
      return { companies: [], active: null }
    }
  }
}
